import React, { useCallback, useEffect, useState } from "react";
import moment from "moment";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";

import { getServerTime, useOpenSimuladao } from "../utils";
import { doFeedbackRequest } from "../index";

import LoadingSimulated from "./Loading";
import { Result } from "../components/Result";
import SimulatedContainer from "../components/container";
import AnswersRegistered from "../components/answersRegistered";
import BlockedCard from "../components/BlockedCard";
import SimulatedHeader from "../components/Header";
import * as S from "./styles";
import RenderModal from "../../components/renderModal";
import ModalLead from "../../components/ModalLead";
import {
  SUBSCRIPTION_KEY,
  WHATSAPP_GROUP_URL,
  AULAS_HOJE_URL,
} from "../../core/storageKeys";

// Simulado fixo desta rota.
const SLUG = "simuladinho-craques-do-enem2026";

const STEPS = {
  LOADING: -1,
  WAITING_FEEDBACK: 2,
  FEEDBACK: 3,
  BLOCKED: 4,
  ERROR: 9,
};

const getUser = () => {
  try {
    return JSON.parse(localStorage.getItem(SUBSCRIPTION_KEY));
  } catch (e) {
    return null;
  }
};

const isValidFeedback = (response) =>
  response && !response.error && Array.isArray(response.resolutions);

const Gabarito = (props) => {
  const { history, feedback, ranking, average, doFeedbackRequest } = props;

  // Híbrido: com login (SUBSCRIPTION_KEY) o backend devolve o resultado
  // personalizado; sem login, devolve apenas o gabarito (respostas corretas).
  const [user] = useState(getUser);
  // Mesma fonte do Zoing: traz o simulado com rankingStartDate/startDate/endDate.
  const [simulated, , , , , simulatedError] = useOpenSimuladao(SLUG, history);
  const [serverTime, setServerTime] = useState(null);
  const [step, setStep] = useState(STEPS.LOADING);
  const [beforeTimeSimulation, setBeforeTimeSimulation] = useState(false);
  // Incrementado quando o contador do BLOCKED zera, para reavaliar o passo.
  const [refreshToken, setRefreshToken] = useState(0);
  const handleCounterComplete = useCallback(
    () => setRefreshToken((token) => token + 1),
    [],
  );

  useEffect(() => {
    let active = true;
    getServerTime().then((time) => {
      if (active) setServerTime(time);
    });
    return () => {
      active = false;
    };
  }, []);

  // Carrega o gabarito e, se vier ok, exibe o resultado; senão, tela de erro.
  const loadFeedback = async () => {
    const response = await doFeedbackRequest(SLUG);
    setStep(isValidFeedback(response) ? STEPS.FEEDBACK : STEPS.ERROR);
  };

  useEffect(() => {
    let active = true;

    (async () => {
      // Falha ao buscar o simulado (backend offline/instável): NÃO redireciona.
      if (simulatedError) {
        setStep(STEPS.ERROR);
        return;
      }

      // Sem dados no localStorage: a tela exibe o modal "Craques do Enem".
      if (!user) return;

      // Logado: precisa do simulado + tempo do servidor pra decidir.
      if (!simulated || !serverTime) return;

      setBeforeTimeSimulation(
        moment(simulated.endDate).isAfter(new Date(serverTime)),
      );

      // Simulado ainda não começou => BLOCKED (tela "o simulado abre em...").
      if (
        simulated.startDate &&
        moment(simulated.startDate).isAfter(new Date(serverTime))
      ) {
        setStep(STEPS.BLOCKED);
        return;
      }

      const isGabaritoReleased = moment(simulated.rankingStartDate).isBefore(
        new Date(serverTime),
      );

      // Respeita a data: antes da liberação, exibe a tela de aguardando gabarito.
      if (!isGabaritoReleased) {
        setStep(STEPS.WAITING_FEEDBACK);
        return;
      }

      const response = await doFeedbackRequest(SLUG);
      if (active) setStep(isValidFeedback(response) ? STEPS.FEEDBACK : STEPS.ERROR);
    })();

    return () => {
      active = false;
    };
  }, [user, simulated, simulatedError, serverTime, refreshToken]);

  // Sem dados no localStorage para identificar o aluno => abre o modal de
  // cadastro/acesso (Craques do Enem). Ao enviar, os dados são salvos no
  // localStorage e a página recarrega para carregar o gabarito.
  if (!user) {
    return (
      <RenderModal>
        <ModalLead
          toggleModal={() => window.location.reload()}
          isSimulatedInfoPage={true}
          hideClose={true}
          buttonLabel="Visualizar gabarito"
        />
      </RenderModal>
    );
  }

  if (step === STEPS.ERROR) {
    return (
      <SimulatedContainer>
        <S.BlockedScreen>
          <SimulatedHeader title="Gabarito Simulado" />
          <S.BlockedSection>
            <BlockedCard
              badgeIcon="⚠️"
              badgeLabel="FALHA DE CONEXÃO"
              title="Não conseguimos carregar o gabarito."
              subtitle="Pode ser uma instabilidade momentânea. Verifique sua conexão e tente novamente em instantes."
              actionLabel="Tentar novamente"
              onActionClick={() => window.location.reload()}
            />
          </S.BlockedSection>
        </S.BlockedScreen>
      </SimulatedContainer>
    );
  }

  if (step === STEPS.LOADING) {
    return (
      <S.Container>
        <LoadingSimulated />
      </S.Container>
    );
  }

  if (step === STEPS.BLOCKED) {
    return (
      <SimulatedContainer>
        <S.BlockedScreen>
          <SimulatedHeader simulated={simulated} title="Gabarito Simulado" />
          <S.BlockedSection>
            <S.BlockedStarYellow>
              <svg
                viewBox="0 0 28.5317 27.1353"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <path
                  d="M14.2658 0L17.6336 10.3647H28.5317L19.7149 16.7705L23.0826 27.1353L14.2658 20.7295L5.44907 27.1353L8.81678 16.7705L0 10.3647H10.8981L14.2658 0Z"
                  fill="#F7D518"
                  fillOpacity="0.6"
                />
              </svg>
            </S.BlockedStarYellow>
            <S.BlockedStarBlue>
              <svg
                viewBox="0 0 20.9232 19.8992"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <path
                  d="M10.4616 0L12.9313 7.60081H20.9232L14.4576 12.2984L16.9273 19.8992L10.4616 15.2016L3.99598 19.8992L6.46564 12.2984L0 7.60081H7.99197L10.4616 0Z"
                  fill="#0033A0"
                  fillOpacity="0.4"
                />
              </svg>
            </S.BlockedStarBlue>
            <S.BlockedStarRed>
              <svg
                viewBox="0 0 17.119 16.2812"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <path
                  d="M8.55951 0L10.5801 6.21885H17.119L11.8289 10.0623L13.8496 16.2812L8.55951 12.4377L3.26944 16.2812L5.29007 10.0623L0 6.21885H6.53888L8.55951 0Z"
                  fill="#D62828"
                  fillOpacity="0.35"
                />
              </svg>
            </S.BlockedStarRed>
            <BlockedCard
              releaseDate={simulated.startDate}
              onComplete={handleCounterComplete}
              onActionClick={() =>
                window.open(AULAS_HOJE_URL, "_blank", "noopener,noreferrer")
              }
            />
          </S.BlockedSection>
        </S.BlockedScreen>
      </SimulatedContainer>
    );
  }

  if (step === STEPS.WAITING_FEEDBACK) {
    return (
      <SimulatedContainer>
        <AnswersRegistered
          history={history}
          slug={SLUG}
          simulated={simulated}
          getRanking={() => {}}
          onFeedbackRelease={loadFeedback}
          whatsappUrl={WHATSAPP_GROUP_URL}
        />
      </SimulatedContainer>
    );
  }

  return (
    <SimulatedContainer>
      <Result
        beforeTimeSimulation={beforeTimeSimulation}
        user={user}
        history={history}
        ranking={ranking}
        feedback={feedback}
        average={average}
        simulated={simulated}
        slug={SLUG}
        serverTime={serverTime}
      />
    </SimulatedContainer>
  );
};

const mapStateToProps = (state) => ({
  feedback: state.simulation.data.feedback,
  ranking: state.simulation.data.ranking,
  average: state.simulation.data.simulation.average,
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      doFeedbackRequest,
    },
    dispatch,
  );

export default connect(mapStateToProps, mapDispatchToProps)(Gabarito);
